export interface HttpRequestOptions {
  url: string;
  method: string;
  headers?: Record<string, string>;
  body?: string | Uint8Array | Record<string, unknown>;
  timeout?: number;
}

export interface HttpResponse<T = unknown> {
  success: boolean;
  data?: T;
  error?: string;
  status?: number;
}

const DEFAULT_TIMEOUT = 20000;

export class HttpClient {
  private abortControllers = new Set<AbortController>();

  async request<T = unknown>(options: HttpRequestOptions): Promise<HttpResponse<T>> {
    const controller = new AbortController();
    this.abortControllers.add(controller);

    const timeoutId = setTimeout(
      () => controller.abort(),
      options.timeout ?? DEFAULT_TIMEOUT,
    );

    try {
      const fetchOptions: RequestInit = {
        method: options.method,
        headers: options.headers,
        signal: controller.signal,
        credentials: 'omit',
      };

      if (options.body && ['POST', 'PUT', 'PATCH'].includes(options.method.toUpperCase())) {
        if (typeof options.body === 'string') {
          fetchOptions.body = options.body;
        } else if (options.body instanceof Uint8Array) {
          fetchOptions.body = options.body as unknown as BodyInit;
        } else {
          fetchOptions.body = JSON.stringify(options.body);
          fetchOptions.headers = {
            'Content-Type': 'application/json',
            ...fetchOptions.headers,
          };
        }
      }

      const response = await fetch(options.url, fetchOptions);

      if (!response.ok) {
        return {
          success: false,
          error: `HTTP ${response.status}: ${response.statusText}`,
          status: response.status,
        };
      }

      const contentType = response.headers.get('content-type');
      const data = contentType?.includes('application/json')
        ? await response.json()
        : await response.text();

      return { success: true, data: data as T, status: response.status };
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        return { success: false, error: 'Request aborted or timed out' };
      }
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    } finally {
      clearTimeout(timeoutId);
      this.abortControllers.delete(controller);
    }
  }

  async executeSignedRequest<T = unknown>(signedRequest: import('../types').SignedRequest): Promise<HttpResponse<T>> {
    let body: string | Uint8Array | undefined;

    if (signedRequest.body && typeof signedRequest.body === 'string') {
      try {
        const binaryString = atob(signedRequest.body);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        body = bytes;
      } catch {
        body = signedRequest.body;
      }
    }

    return this.request<T>({
      url: signedRequest.url,
      method: signedRequest.method,
      headers: signedRequest.headers,
      body,
    });
  }

  abortAll(): void {
    for (const controller of this.abortControllers) {
      controller.abort();
    }
    this.abortControllers.clear();
  }
}
